/**
 * Biometric authentication wrapper (#35 — IOS-4.2).
 *
 * Thin wrapper around @aparajita/capacitor-biometric-auth so the rest of the
 * app never imports the plugin directly. All methods are no-ops on web/PWA.
 *
 * Persistence of the "user has enrolled biometrics" flag lives in pfhApi.ts
 * (Keychain via @capacitor/preferences) to keep all storage in one place.
 */

import { Capacitor } from '@capacitor/core'
import { BiometricAuth, BiometryType } from '@aparajita/capacitor-biometric-auth'

export type BiometricAvailability =
  | { available: true; type: BiometryType }
  | { available: false; reason: string }

/**
 * Returns whether biometric auth is available on this device.
 * Always returns `{ available: false }` on web.
 */
export async function checkBiometricAvailability(): Promise<BiometricAvailability> {
  if (!Capacitor.isNativePlatform()) {
    return { available: false, reason: 'Not running natively' }
  }
  try {
    const { isAvailable, biometryType, reason } = await BiometricAuth.checkBiometry()
    if (isAvailable) {
      return { available: true, type: biometryType }
    }
    return { available: false, reason: reason ?? 'Biometry unavailable' }
  } catch {
    return { available: false, reason: 'Biometry check failed' }
  }
}

/**
 * Prompt the user to authenticate with Face ID / Touch ID.
 * Returns true on success, false on failure or cancellation.
 * Always returns false on web.
 */
export async function authenticate(reason: string): Promise<boolean> {
  if (!Capacitor.isNativePlatform()) return false
  try {
    await BiometricAuth.authenticate({
      reason,
      cancelTitle: 'Use password',
      allowDeviceCredential: false,
    })
    return true
  } catch {
    return false
  }
}

/**
 * Human-readable label for the available biometry type (e.g. "Face ID", "Touch ID").
 */
export function biometryLabel(type: BiometryType): string {
  switch (type) {
    case BiometryType.faceId:       return 'Face ID'
    case BiometryType.touchId:      return 'Touch ID'
    case BiometryType.faceAuthentication: return 'Face Authentication'
    case BiometryType.fingerprintAuthentication: return 'Fingerprint'
    case BiometryType.irisAuthentication: return 'Iris Authentication'
    default: return 'Biometrics'
  }
}
